import { useMemo } from "react";
import {
  useTestResults,
  type TestResult,
} from "@src/packages/test-results/hooks/useTestResults";

export type AmhUnit = "ng/mL" | "pmol/L";
export type FshUnit = "mIU/mL" | "IU/L";

export type ReserveInputs = {
  amh?: { value: number; unit: AmhUnit };
  afc?: number;
  fsh?: { value: number; unit: FshUnit };
};

/**
 * Normalize a test-result name for tolerant matching: strip diacritics
 * (Anti-Müllerian → Anti-Mullerian) and lowercase. NFKD splits combining
 * accents; the regex range ̀-ͯ drops them.
 */
function normalizeName(name: string | null | undefined): string {
  return (name ?? "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * Strict AMH-unit resolver: only returns a unit when the stored string
 * unambiguously matches ng/mL or pmol/L. Missing or unrecognized units
 * return null so callers can skip the record rather than silently
 * defaulting — ng/mL vs pmol/L is a ~7× difference and mis-defaulting
 * changes the estimate materially.
 */
export function resolveAmhUnit(
  unit: string | null | undefined,
): AmhUnit | null {
  const u = (unit ?? "").toLowerCase().replace(/\s+/g, "");
  if (!u) return null;
  if (u.includes("pmol")) return "pmol/L";
  if (u.includes("ng/ml") || u === "ngml" || u === "ng") return "ng/mL";
  return null;
}

/** Non-strict fallback (used only for the sheet's default toggle state). */
export function parseAmhUnit(unit: string | null | undefined): AmhUnit {
  return resolveAmhUnit(unit) ?? "ng/mL";
}

export function parseFshUnit(unit: string | null | undefined): FshUnit {
  const n = (unit ?? "").toLowerCase();
  return n.includes("iu/l") && !n.includes("miu") ? "IU/L" : "mIU/mL";
}

export function toNumericResult(r: TestResult): number | null {
  const raw = r.patientValue ?? r.value;
  if (raw == null) return null;
  const n = parseFloat(String(raw));
  return Number.isFinite(n) ? n : null;
}

function sortByDateDesc(results: TestResult[]): TestResult[] {
  return [...results].sort((a, b) => {
    const da = a.date ?? a.createdAt ?? "";
    const db = b.date ?? b.createdAt ?? "";
    return db.localeCompare(da);
  });
}

export function pickLatest(matches: TestResult[]): TestResult | null {
  const sorted = sortByDateDesc(matches);
  return sorted[0] ?? null;
}

export function isAmhResult(r: TestResult): boolean {
  const name = normalizeName(r.name);
  return name.includes("amh") || name.includes("anti-mullerian");
}

export function isAfcResult(r: TestResult): boolean {
  return (
    r.imagingTestCategory === "antral_follicle_count" ||
    normalizeName(r.name).includes("antral follicle")
  );
}

export function isFshResult(r: TestResult): boolean {
  const name = normalizeName(r.name);
  return /(^|\W)fsh(\W|$)/.test(name) || name.includes("follicle-stimulating");
}

export type AmhResult = {
  id: string;
  value: number;
  unit: AmhUnit;
  date: string | null;
};

export type AfcResult = {
  id: string;
  value: number;
  date: string | null;
};

export function useAfcResults(): AfcResult[] {
  const { data: testResults } = useTestResults();

  return useMemo(() => {
    if (!testResults || !Array.isArray(testResults)) return [];
    const matches = testResults.filter(
      (r) => isAfcResult(r) && toNumericResult(r) !== null,
    );
    const mapped: (AfcResult | null)[] = sortByDateDesc(matches).map((r) => {
      const value = toNumericResult(r);
      if (value === null || value < 0) return null;
      return {
        id: r.id,
        value: Math.round(value),
        date: r.date ?? r.createdAt ?? null,
      };
    });
    return mapped.filter((x): x is AfcResult => x !== null);
  }, [testResults]);
}

export function useAmhResults(): AmhResult[] {
  const { data: testResults } = useTestResults();

  return useMemo(() => {
    if (!testResults || !Array.isArray(testResults)) return [];
    const matches = testResults.filter(
      (r) => isAmhResult(r) && toNumericResult(r) !== null,
    );
    const mapped: (AmhResult | null)[] = sortByDateDesc(matches).map((r) => {
      const value = toNumericResult(r);
      const unit = resolveAmhUnit(r.unit);
      // Skip AMH records without an explicit, recognizable unit —
      // ng/mL vs pmol/L differ by ~7×, silently assuming a default
      // would meaningfully mis-drive the estimate.
      if (value === null || value <= 0 || unit === null) return null;
      return {
        id: r.id,
        value,
        unit,
        date: r.date ?? r.createdAt ?? null,
      };
    });
    return mapped.filter((x): x is AmhResult => x !== null);
  }, [testResults]);
}

export function useOvarianReserveInputs(): ReserveInputs {
  const { data: testResults } = useTestResults();

  return useMemo(() => {
    if (!testResults || !Array.isArray(testResults)) return {};

    const withValue = testResults.filter((r) => toNumericResult(r) !== null);
    const amh = pickLatest(withValue.filter(isAmhResult));
    const afc = pickLatest(withValue.filter(isAfcResult));
    const fsh = pickLatest(withValue.filter(isFshResult));

    const inputs: ReserveInputs = {};
    if (amh) {
      const value = toNumericResult(amh);
      const unit = resolveAmhUnit(amh.unit);
      if (value !== null && value > 0 && unit !== null) {
        inputs.amh = { value, unit };
      }
    }
    if (afc) {
      const value = toNumericResult(afc);
      if (value !== null && value >= 0) {
        inputs.afc = Math.round(value);
      }
    }
    if (fsh) {
      const value = toNumericResult(fsh);
      if (value !== null && value > 0) {
        inputs.fsh = { value, unit: parseFshUnit(fsh.unit) };
      }
    }
    return inputs;
  }, [testResults]);
}
